import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Loader2, RotateCcw, Save, Languages } from 'lucide-react';
import { toast } from 'sonner';

// Groups of keys (base slug without _it/_en suffix)
const GROUPS = [
  {
    label: 'Schermata Accesso',
    bases: ['welcome_heading', 'welcome_subtitle', 'expires_label', 'checkin_label', 'checkout_label'],
  },
  {
    label: 'Navigazione Tab',
    bases: ['tab_home', 'tab_access', 'tab_info', 'tab_activity'],
  },
  {
    label: 'Dispositivi',
    bases: ['devices_title'],
  },
  {
    label: 'Sezione Info',
    bases: ['wifi_section_title', 'wifi_network_label', 'wifi_password_label', 'house_rules_title', 'emergency_title'],
  },
  {
    label: 'Registro Attività',
    bases: ['activity_title', 'no_activity'],
  },
  {
    label: 'Chiave Scaduta',
    bases: ['expired_title', 'expired_subtitle'],
  },
  {
    label: 'Link Non Valido',
    bases: ['invalid_title', 'invalid_subtitle'],
  },
];

export default function Translations() {
  const [strings, setStrings] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/i18n');
      setStrings(res.data);
      setEdits({});
    } catch {
      toast.error('Errore caricamento testi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getVal = (key) =>
    edits[key] !== undefined
      ? edits[key]
      : (strings.find(s => s.key === key)?.value || '');

  const saveAll = async () => {
    const changed = Object.keys(edits);
    if (changed.length === 0) { toast.info('Nessuna modifica da salvare'); return; }
    setSaving(true);
    try {
      await api.put('/i18n/bulk', edits);
      toast.success(`${changed.length} testo/i salvato/i`);
      load();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const resetKey = async (key) => {
    try {
      const res = await api.delete(`/i18n/${key}/reset`);
      setStrings(prev => prev.map(s =>
        s.key === key ? { ...s, value: res.data.value, is_customized: false } : s
      ));
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Testo ripristinato al valore predefinito');
    } catch {
      toast.error('Errore reset');
    }
  };

  const hasChanges = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Personalizzazione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Testi App Ospite</h2>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-amber-400 text-xs">
              {Object.keys(edits).length} modifica/e non salvata/e
            </span>
          )}
          <button
            onClick={saveAll}
            disabled={saving || !hasChanges}
            className="btn-gold flex items-center gap-2"
            data-testid="save-translations-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salva
          </button>
        </div>
      </div>

      <div className="card-dark rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Languages size={16} className="text-[#c9a84c] shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[var(--lux-text-muted)] text-sm leading-relaxed">
            Modifica i testi mostrati nella <strong className="text-white">Guest App</strong> per ogni lingua (IT/EN).
            Le modifiche si applicano in tempo reale a tutti gli ospiti. Usa il pulsante
            <span className="inline-flex items-center gap-1 mx-1 text-[var(--lux-text-muted-3)]"><RotateCcw size={10} /> ripristina</span>
            per tornare al valore predefinito.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-[#c9a84c]" />
        </div>
      ) : (
        <div className="space-y-6">
          {GROUPS.map(({ label, bases }) => {
            const groupStrings = bases.flatMap(base => [
              strings.find(s => s.key === `${base}_it`),
              strings.find(s => s.key === `${base}_en`),
            ]).filter(Boolean);
            if (groupStrings.length === 0) return null;

            return (
              <div key={label} className="card-dark rounded-lg overflow-hidden">
                <div className="px-5 py-3 border-b border-[var(--lux-border-1)] bg-[var(--lux-surface-1)]">
                  <p className="text-[#c9a84c] text-xs uppercase tracking-widest">{label}</p>
                </div>
                <div className="divide-y divide-[var(--lux-surface-2)]">
                  {groupStrings.map(s => {
                    const isIt = s.key.endsWith('_it');
                    const isEdited = edits[s.key] !== undefined;
                    const currentVal = getVal(s.key);

                    return (
                      <div key={s.key} className="px-5 py-4 flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded border ${isIt ? 'border-green-600/40 text-green-400 bg-green-900/20' : 'border-blue-600/40 text-blue-400 bg-blue-900/20'}`}>
                              {isIt ? 'IT' : 'EN'}
                            </span>
                            <span className="text-[var(--lux-border-4)] text-[10px] font-mono truncate">{s.key}</span>
                            {s.is_customized && !isEdited && (
                              <span className="text-amber-400 text-[10px] shrink-0">● personalizzato</span>
                            )}
                            {isEdited && (
                              <span className="text-blue-400 text-[10px] shrink-0">● in modifica</span>
                            )}
                          </div>
                          <input
                            className="input-dark w-full text-sm"
                            value={currentVal}
                            onChange={e => setEdits(p => ({ ...p, [s.key]: e.target.value }))}
                            data-testid={`i18n-input-${s.key}`}
                          />
                          {isEdited && (
                            <p className="text-[var(--lux-border-4)] text-xs mt-1">
                              Salvato: <span className="text-[var(--lux-text-muted-3)]">{s.value}</span>
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => resetKey(s.key)}
                          className="text-[var(--lux-border-3)] hover:text-[var(--lux-text-muted)] transition-colors mt-8 shrink-0"
                          title="Ripristina valore predefinito"
                          data-testid={`i18n-reset-${s.key}`}
                        >
                          <RotateCcw size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
